/**
 * Marca activa (ADR-035). Se lee de la env EN CADA LLAMADA, no al cargar el
 * módulo: así los tests pueden `vi.stubEnv` en beforeEach. `NEXT_PUBLIC_BRAND`
 * se accede como literal para que Next lo incruste en el bundle cliente.
 *
 * Solo importa lib/locale-prefix (que a su vez solo importa el contrato de
 * idiomas del dominio): este módulo llega al middleware (edge) y al cliente.
 */

import { getLocalePrefix } from '@/lib/locale-prefix'
import {
  BRANDS,
  DEFAULT_BRAND_ID,
  isBrandId,
  type Brand,
  type BrandFamily,
  type BrandHandover,
  type BrandId,
} from './registry'

export type { Brand, BrandFamily, BrandHandover, BrandId, HandoverMode } from './registry'
export { isBrandId } from './registry'

export function getBrandId(): BrandId {
  const raw = process.env.NEXT_PUBLIC_BRAND
  return isBrandId(raw) ? raw : DEFAULT_BRAND_ID
}

export function getBrand(): Brand {
  return BRANDS[getBrandId()]
}

/**
 * URL pública del despliegue: `NEXT_PUBLIC_APP_URL` manda; sin ella, la
 * `productionUrl` de la marca; sin barra final.
 *
 * `NEXT_PUBLIC_APP_URL` sólo manda si de verdad es una URL absoluta. Un valor
 * basura —el caso real: la cadena `"undefined"`, que es lo que deja
 * `process.env.X = undefined` en Node— no debe ganarle a la `productionUrl`
 * de la marca, que siempre es válida porque sale del registro. Sin esta guarda
 * el valor basura viajaba hasta `new URL()` en `site-metadata.ts`, que se
 * evalúa al IMPORTAR el módulo: una env mal puesta no degradaba una página,
 * tumbaba la importación de los dos root layouts y con ella todo lo que
 * cuelgue de ellos.
 */
export function siteUrlFor(brand: Brand): string {
  const raw = process.env.NEXT_PUBLIC_APP_URL
  return (isAbsoluteUrl(raw) ? raw : brand.productionUrl).replace(/\/$/, '')
}

/**
 * `try`/`catch` y no `URL.canParse`: este módulo lo alcanza código de cliente,
 * incluida la WebView de la app nativa, y `canParse` es de 2023 — en un motor
 * que no lo traiga, la guarda misma sería el `TypeError`. `new URL` está en
 * todos.
 */
function isAbsoluteUrl(value: string | undefined): value is string {
  if (!value) return false
  try {
    new URL(value)
    return true
  } catch {
    return false
  }
}

export function getSiteUrl(): string {
  return siteUrlFor(getBrand())
}

/**
 * Origen público donde una FAMILIA es canónica: el despliegue activo si la sirve
 * y no la traspasa; si no, la marca del registro que la sirve sin traspasarla
 * (para un traspaso, `validateHandover` garantiza que el destino la tiene en
 * `families`). Es la pregunta que el hreflang cruzado necesita en los dos
 * sentidos (#435): papanoel.chat anuncia al Ratón en ratonperez.app y
 * ratonperez.app anuncia a la Hada en papanoel.chat. `productionUrl` y no la env:
 * es OTRA marca, su `NEXT_PUBLIC_APP_URL` no existe en este despliegue.
 */
export function getFamilyOrigin(family: BrandFamily): string {
  if (isFamilyEnabled(family) && !isHandedOver(family)) return getSiteUrl()
  const owner = Object.values(BRANDS).find((brand) => servesCanonically(brand, family))
  if (!owner) throw new Error(`ninguna marca del registro sirve la familia ${family} sin traspasarla`)
  return owner.productionUrl
}

/** ¿Es esta marca la dueña canónica de la familia: la sirve y no la traspasa? */
function servesCanonically(brand: Brand, family: BrandFamily): boolean {
  return brand.families.includes(family) && (brand.handover?.[family]?.mode ?? 'none') === 'none'
}

/** Origen del catálogo genérico (guías `easter`, landings del catálogo): el activo si lo sirve; si no, la marca que lo sirve. */
export function getCatalogOrigin(): string {
  if (isCatalogEnabled()) return getSiteUrl()
  const owner = Object.values(BRANDS).find((brand) => brand.catalog)
  if (!owner) throw new Error('ninguna marca del registro sirve el catálogo genérico')
  return owner.productionUrl
}

export function isPrimaryBrand(): boolean {
  return getBrand().primary
}

export function isFamilyEnabled(family: BrandFamily): boolean {
  return getBrand().families.includes(family)
}

/**
 * ¿Ofrece la marca activa el personaje propio? Es solo la parte de MARCA: para un
 * usuario manda además el kill-switch `CUSTOM_CHARACTER_ENABLED` (`isCustomCharacterOffered`) y
 * para la landing la env de despliegue (`servesCustomCharacterLanding`).
 */
export function brandOffersCustomCharacter(): boolean {
  return getBrand().customCharacter
}

export function isCatalogEnabled(): boolean {
  return getBrand().catalog
}

export function isTrackerEnabled(): boolean {
  return getBrand().tracker
}

/** ¿Es esta familia la que ocupa `/` en la marca activa? */
export function isHomeFamily(family: BrandFamily): boolean {
  return getBrand().home === family
}

/** Puerta de acceso canónica de la marca, con el prefijo del locale (es sin prefijo). */
export function getBrandAuthHref(locale: string): string {
  return `${getLocalePrefix(locale)}/${getBrand().authSlug}`
}

export function getHandover(family: BrandFamily): BrandHandover | undefined {
  return getBrand().handover?.[family]
}

/** ¿Se ha traspasado esta familia a otro dominio? (fase 8: canonical/redirect, ver ADR-035 y #308). */
export function isHandedOver(family: BrandFamily): boolean {
  return (getHandover(family)?.mode ?? 'none') !== 'none'
}

/**
 * Origen (sin barra final) al que se traspasa una familia; undefined sin traspaso o con `mode: 'none'`.
 * El registro ya prohíbe la barra final (`validateHandover`): el `replace` es defensa, no contrato.
 */
export function handoverOriginOf(handover: BrandHandover | undefined): string | undefined {
  if (!handover || handover.mode === 'none') return undefined
  return handover.url.replace(/\/$/, '')
}

/** Origen destino de la familia traspasada por la marca activa (ver `handoverOriginOf`). */
export function getHandoverOrigin(family: BrandFamily): string | undefined {
  return handoverOriginOf(getHandover(family))
}

/** Traspaso activo de una familia, con el origen ya estrechado a `string`. Único punto de decisión de las páginas. */
export type ActiveHandover = { mode: 'canonical' | 'redirect'; origin: string }

/**
 * Traspaso activo de una familia (ADR-035 fase 8): `undefined` sin entrada o con `mode: 'none'`.
 * Las tres superficies traspasables (landing, acceso y guías) deciden SOLO con esto.
 */
export function resolveHandover(family: BrandFamily): ActiveHandover | undefined {
  const handover = getHandover(family)
  const origin = handoverOriginOf(handover)
  if (!handover || handover.mode === 'none' || !origin) return undefined
  return { mode: handover.mode, origin }
}

export class BrandEnvError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'BrandEnvError'
  }
}

type BrandEnv = { NEXT_PUBLIC_BRAND?: string; NEXT_PUBLIC_APP_URL?: string }

const LOCAL_URL = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/?$/

/**
 * Fail-closed de configuración: una marca distinta de la primaria sin su
 * propia `NEXT_PUBLIC_APP_URL` publicaría canonicals, sitemap y retornos de
 * pago del dominio de Papá Noel. Mejor romper el build que descubrirlo en GSC.
 */
export function assertBrandEnv(
  env: BrandEnv = {
    NEXT_PUBLIC_BRAND: process.env.NEXT_PUBLIC_BRAND,
    NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
  }
): void {
  const id = isBrandId(env.NEXT_PUBLIC_BRAND) ? env.NEXT_PUBLIC_BRAND : DEFAULT_BRAND_ID
  if (id === DEFAULT_BRAND_ID) return
  const url = env.NEXT_PUBLIC_APP_URL ?? ''
  if (!url) {
    throw new BrandEnvError(`NEXT_PUBLIC_BRAND=${id} requiere NEXT_PUBLIC_APP_URL con el dominio de la marca`)
  }
  if (!LOCAL_URL.test(url) && url.includes('papanoel.chat')) {
    throw new BrandEnvError(`NEXT_PUBLIC_BRAND=${id} no puede servirse desde papanoel.chat (NEXT_PUBLIC_APP_URL=${url})`)
  }
}

// En servidor y en el build (también edge) la aserción corre al cargar el
// módulo; en el navegador `window` existe y se salta (el build ya la pasó).
if (typeof window === 'undefined') {
  assertBrandEnv()
}
